import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, campaignsApi, statsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  Users, Calendar, Upload, BarChart3, Database, TrendingUp,
  Activity, CheckCircle2, AlertTriangle, ArrowRight, Loader2, Sparkles,
} from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
} from 'recharts';

function Stat({ label, value, icon: Icon, tint = 'bg-aea-blue/10 text-aea-blue', testId }) {
  return (
    <Card className="aea-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</div>
            <div className="text-3xl font-outfit font-semibold mt-2" data-testid={testId}>{value}</div>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}>
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminDashboard() {
  const [d, setD] = useState(null);
  const [sector, setSector] = useState(null);
  useEffect(() => {
    dashboardApi.admin().then((r) => setD(r.data)).catch(() => {});
    statsApi.sector().then((r) => setSector(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-aea-blue" /></div>;

  const chartData = (sector?.indicators || []).filter(i => !i.insufficient_data).slice(0, 6).map((i) => ({
    name: i.indicator_name?.split(' ').slice(0, 2).join(' ') || i.indicator_code,
    mean: Math.round(i.mean),
    median: Math.round(i.median),
  }));

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Empresas asociadas" value={d.total_empresas} icon={Users} testId="stat-empresas" />
        <Stat label="Validadas" value={d.empresas_validadas} icon={CheckCircle2} tint="bg-emerald-50 text-emerald-600" testId="stat-validadas" />
        <Stat label="Campañas activas" value={d.open_campaigns} icon={Calendar} tint="bg-amber-50 text-amber-600" testId="stat-open-campaigns" />
        <Stat label="Puntos de datos" value={d.total_datapoints.toLocaleString('es-ES')} icon={Database} tint="bg-sky-50 text-sky-600" testId="stat-datapoints" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="aea-card lg:col-span-2">
          <CardHeader><CardTitle className="text-lg">Indicadores del último periodo · Media vs Mediana</CardTitle></CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">Sin datos suficientes para mostrar gráfico</div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="mean" name="Media" fill="#0369A1" radius={[4,4,0,0]} />
                    <Bar dataKey="median" name="Mediana" fill="#94A3B8" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="text-xs text-slate-500 mt-2">Periodo: {sector?.period_label || '—'}</div>
          </CardContent>
        </Card>

        <Card className="aea-card">
          <CardHeader><CardTitle className="text-lg">Envíos</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between border-b pb-2">
              <div className="text-sm text-slate-600">Total envíos</div>
              <div className="font-mono font-semibold">{d.total_submissions}</div>
            </div>
            <div className="flex justify-between border-b pb-2">
              <div className="text-sm text-slate-600">Válidos</div>
              <div className="font-mono font-semibold text-emerald-600">{d.submissions_ok}</div>
            </div>
            <div className="flex justify-between border-b pb-2">
              <div className="text-sm text-slate-600">Con errores</div>
              <div className="font-mono font-semibold text-red-600">{d.submissions_err}</div>
            </div>
            <div className="flex justify-between pb-2">
              <div className="text-sm text-slate-600">Pendientes validación</div>
              <div className="font-mono font-semibold text-amber-600">{d.pendientes_validacion}</div>
            </div>
            <Link to="/empresas"><Button className="w-full bg-aea-blue hover:bg-aea-blue-dk" data-testid="btn-goto-empresas">Gestionar empresas <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmpresaDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [bench, setBench] = useState(null);
  useEffect(() => {
    dashboardApi.company().then((r) => setD(r.data)).catch(() => {});
    statsApi.benchmark(user.id).then((r) => setBench(r.data)).catch(() => {});
  }, [user.id]);

  if (!d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-aea-blue" /></div>;

  const benchChart = (bench?.benchmark || []).filter(b => !b.sector.insufficient_data).slice(0, 6).map((b) => ({
    name: b.indicator_code.replace(/_/g, ' ').slice(0, 14),
    Vd: b.my_value,
    Sector: Math.round(b.sector.mean),
  }));

  return (
    <div className="space-y-6 animate-fade-in-up">
      {d.incorporation_status !== 'effective' && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <div className="font-semibold">Incorporación pendiente</div>
              <div className="mt-1">
                {!d.contract_signed
                  ? <>Firme el contrato de adhesión para continuar. <Link to="/mi-contrato" className="underline font-medium">Ir a Mi contrato</Link></>
                  : <>Su alta está pendiente de validación por el equipo AEA. Le avisaremos por email.</>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Envíos realizados" value={d.my_submissions} icon={Upload} testId="stat-my-subs" />
        <Stat label="Envíos válidos" value={d.my_valid_submissions} icon={CheckCircle2} tint="bg-emerald-50 text-emerald-600" testId="stat-my-valid" />
        <Stat label="Mis datos activos" value={d.my_datapoints} icon={Database} tint="bg-sky-50 text-sky-600" testId="stat-my-datapoints" />
        <Stat label="Campañas abiertas" value={d.open_campaigns_count} icon={Calendar} tint="bg-amber-50 text-amber-600" testId="stat-open-campaigns" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="aea-card lg:col-span-2">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-aea-blue" /> Vd. vs Sector · {bench?.period_label || 'último periodo'}</CardTitle></CardHeader>
          <CardContent>
            {benchChart.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">Sin datos suficientes para comparativa. Suba sus datos primero.</div>
            ) : (
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={benchChart} margin={{ top: 8, right: 16, left: 0, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Vd" name="Su empresa" fill="#0369A1" radius={[4,4,0,0]} />
                    <Bar dataKey="Sector" name="Media sector" fill="#94A3B8" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="aea-card">
          <CardHeader><CardTitle className="text-lg">Acciones rápidas</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Link to="/subir-datos"><Button className="w-full bg-aea-blue hover:bg-aea-blue-dk justify-start" data-testid="btn-goto-upload"><Upload className="w-4 h-4 mr-2" /> Subir datos del mes</Button></Link>
            <Link to="/benchmark"><Button variant="outline" className="w-full justify-start" data-testid="btn-goto-benchmark"><TrendingUp className="w-4 h-4 mr-2" /> Ver comparativa</Button></Link>
            <Link to="/mis-datos"><Button variant="outline" className="w-full justify-start" data-testid="btn-goto-mydata"><Database className="w-4 h-4 mr-2" /> Mis datos</Button></Link>
            <Link to="/agente-ia"><Button variant="outline" className="w-full justify-start" data-testid="btn-goto-ai"><Sparkles className="w-4 h-4 mr-2" /> Consultar Agente IA</Button></Link>
          </CardContent>
        </Card>
      </div>

      {d.pending_campaigns?.length > 0 && (
        <Card className="aea-card">
          <CardHeader><CardTitle className="text-lg">Campañas pendientes de envío</CardTitle></CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.pending_campaigns.map((c) => (
                <div key={c.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="text-xs uppercase text-aea-blue font-semibold tracking-widest">{c.period_label}</div>
                  <div className="mt-1 font-semibold">{c.title}</div>
                  <Link to="/subir-datos" state={{ campaignId: c.id }}>
                    <Button size="sm" className="mt-3 bg-aea-blue hover:bg-aea-blue-dk">Subir datos</Button>
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin, isAuditor } = useAuth();
  return (isAdmin || isAuditor) ? <AdminDashboard /> : <EmpresaDashboard />;
}
